import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ShieldCheck, CreditCard, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public payment page opened by the client when they scan the QR code shown
 * by the driver. Secured by a per-booking `payment_token` passed in the URL.
 * This page is intentionally minimal — no tracking info, no admin data, just
 * a price and a "Pay" button that redirects to Stripe Checkout.
 */
export default function Pay() {
  const { bookingId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const justPaid = sp.get("paid") === "1";
  const sessionId = sp.get("session_id") || "";

  const [state, setState] = useState({ loading: true, info: null, error: null });
  const [submitting, setSubmitting] = useState(false);

  // When the customer is redirected back from Stripe Checkout with
  // `?paid=1&session_id=...`, ping the backend to reconcile the booking
  // (sets `payment_status=paid`, triggers the driver "Paiement reçu" push,
  // unlocks the «Course terminée» button on the driver UI). This is the
  // safety net used in addition to the Stripe webhook — works even if the
  // webhook is not yet configured on production.
  useEffect(() => {
    if (!justPaid || !sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const reconcile = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          if (!cancelled) setState((s) => ({ ...s, info: { ...(s.info || {}), paid: true } }));
          return true;
        }
      } catch (_) {}
      return false;
    };
    (async () => {
      // Retry up to 6 times (Stripe sometimes settles 1-2 seconds after the redirect)
      while (!cancelled && attempts < 6) {
        const ok = await reconcile();
        if (ok) break;
        attempts += 1;
        await new Promise((r) => setTimeout(r, 1500));
      }
    })();
    return () => { cancelled = true; };
  }, [justPaid, sessionId]);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, info: null, error: "Lien invalide — token manquant" });
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pay/${bookingId}`, { params: { token } });
        setState({ loading: false, info: data, error: null });
      } catch (err) {
        setState({
          loading: false,
          info: null,
          error: err.response?.data?.detail || "Lien invalide ou expiré",
        });
      }
    })();
  }, [bookingId, token]);

  const pay = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/pay/${bookingId}/checkout-session`,
        { origin_url: window.location.origin },
        { params: { token } },
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("URL Stripe indisponible");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Erreur de paiement");
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <Shell>
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-center text-sm text-muted-foreground">Chargement du paiement…</p>
      </Shell>
    );
  }

  if (state.error) {
    return (
      <Shell>
        <div className="grid h-12 w-12 place-items-center rounded-full bg-destructive/15 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h1 className="mt-4 font-[Outfit] text-2xl">Lien de paiement invalide</h1>
        <p className="mt-1 text-sm text-muted-foreground">{state.error}</p>
        <p className="mt-4 text-xs text-muted-foreground">
          Demandez au chauffeur de vous présenter un nouveau QR code.
        </p>
      </Shell>
    );
  }

  const b = state.info?.booking || {};
  const paid = justPaid || state.info?.paid;

  if (paid) {
    return (
      <Shell>
        <div className="grid h-14 w-14 place-items-center rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h1 className="mt-4 font-[Outfit] text-3xl">Paiement confirmé</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Merci ! Votre course Saor est réglée.
        </p>
        <p className="mt-1 text-base font-medium text-foreground" data-testid="paid-amount">
          {b.price ? `${b.price} €` : ""}
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-[10px] uppercase tracking-widest text-primary">
        <ShieldCheck className="h-3 w-3" /> Paiement sécurisé Saor
      </div>
      <h1 className="mt-4 font-[Outfit] text-3xl font-normal tracking-tight">Réglez votre course</h1>
      {b.hotel_name && (
        <p className="mt-1 text-sm text-muted-foreground">
          {b.hotel_name} → {b.arrival_address}
        </p>
      )}

      <div className="mt-6 rounded-2xl border border-border bg-secondary/40 p-5">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Montant à régler</p>
        <p className="mt-1 font-[Outfit] text-4xl font-semibold text-primary" data-testid="pay-amount">
          {b.price} €
        </p>
        {b.vehicle_type_label && (
          <p className="mt-1 text-xs text-muted-foreground">{b.vehicle_type_label}</p>
        )}
      </div>

      <Button
        onClick={pay}
        disabled={submitting || !state.info?.driver_ready}
        size="lg"
        className="mt-6 w-full rounded-full bg-primary hover:bg-primary/90 gap-2"
        data-testid="pay-now-btn"
      >
        <CreditCard className="h-4 w-4" />
        {submitting ? "Redirection vers le paiement…" : "Payer par carte"}
      </Button>

      <p className="mt-3 text-center text-[11px] text-muted-foreground">
        Paiement sécurisé par Stripe. CB, Apple Pay et Google Pay acceptés.
      </p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-md px-5 py-12 sm:px-6 sm:py-20">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8" data-testid="pay-page">
          {children}
        </div>
      </div>
    </div>
  );
}
